import React, { useEffect, useRef } from "react";
import { useFormHandlers } from "@swordsweeper/react-forms";
import { Button } from "@swordsweeper/ssi-ui-component-library";
import classnames from "classnames";

interface FormProps {
  formData: any;
  onChange: (data: any) => void;
  setFormData: (data: any) => void;
  [key: string]: any;
}

interface FormDataController {
  handleUpdate: (data: any) => void;
  setFormData: (data: any) => void;
  getFormData: () => any;
}

interface FormContainerProps {
  onSubmit: (data: any) => void;
  onCancel?: () => void;
  form: React.ComponentType<FormProps>;
  schema: object;
  initialData?: any;
  additionalFormProps?: any;
  submitButtonText?: string;
  submitButtonClassName?: string;
  onReceiveFormDataController?:
    | ((controller: FormDataController) => void)
    | null;
}

export default function FormContainer({
  onSubmit,
  onCancel,
  form: Form,
  schema,
  initialData = {},
  additionalFormProps = {},
  submitButtonText = "Submit",
  submitButtonClassName = "",
  onReceiveFormDataController = null,
}: FormContainerProps): React.JSX.Element {
  const { formData, handleUpdate, setFormData, isValid, invalidFields } =
    useFormHandlers(initialData, schema);

  // Refs to always have access to current values for callbacks
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  const handleUpdateRef = useRef(handleUpdate);
  handleUpdateRef.current = handleUpdate;

  const setFormDataRef = useRef(setFormData);
  setFormDataRef.current = setFormData;

  useEffect(() => {
    if (onReceiveFormDataController) {
      // Use refs to avoid infinite loop - these callbacks delegate to current ref values
      onReceiveFormDataController({
        handleUpdate: (data: any) => handleUpdateRef.current(data),
        setFormData: (data: any) => setFormDataRef.current(data),
        getFormData: () => formDataRef.current,
      });
    }
    // Only run once when onReceiveFormDataController is set
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onReceiveFormDataController]);

  return (
    <div>
      <div className="flex-column spaced-content-v-lg">
        <Form
          formData={formData}
          onChange={handleUpdate}
          setFormData={setFormData}
          {...additionalFormProps}
        />
      </div>

      <div className="flex-row space-between margin-top-md">
        {!!onCancel && (
          <Button
            className="btn btn-primary btn-bordered btn-md"
            onClick={onCancel}
            label={additionalFormProps.isSuccessful ? "Close" : "Cancel"}
            data-testid="form-cancel-button"
          />
        )}
        {!additionalFormProps.isSuccessful && (
          <Button
            className={classnames(
              "btn btn-primary btn-md",
              submitButtonClassName,
            )}
            onClick={() => onSubmit(formData)}
            disabled={!isValid}
            label={submitButtonText}
            data-testid="form-submit-button"
          />
        )}
      </div>
    </div>
  );
}
